import './StillCountingList.css';
import { Icon } from '../../components/Icon';
import { LiveDot } from '../../components/LiveDot';
import { t } from '../../copy/strings';
import { joinList } from '../../format/agree';
import { placeName } from '../../format/places';
import { stillCountingUfs } from '../../model/governors';
import type { ResultsView, UfCode } from '../../model/types';
import { setOffice } from '../../state/actions';
import { useConfig, useLayout, useView } from '../../state/hooks';

export interface StillCountingListProps {
  /** Presidência definida (`HeroModel.decided !== null`): sem ela, nada é renderizado (AC-7.9-03). */
  decided: boolean;
}

/**
 * UFs de governador ainda apurando (§7.9 bloco 3): `status` 'counting' ou 'no_data', sem `mathDecision` nem
 * `tseResult`, em ordem alfabética de sigla. A regra mora em model/governors.ts (`stillCountingUfs`, FE §6.9) desde a
 * Fase 9; o nome fica aqui para os testes do herói. O booleano `governorsCounting` de §11.1.1 (ponto da aba, §6.3) é
 * outro: conta também a disputa completa sem decisão publicada.
 */
export function governorsCounting(governor: ResultsView['governor']): UfCode[] {
  return stillCountingUfs(governor);
}

/** Detalhe da linha (§7.9 bloco 3): 1 UF → nome por extenso; 2 ou 3 → siglas ("AC, AM e RJ"); mais → "{N} estados". */
export function stillCountingDetail(ufs: readonly UfCode[]): string {
  if (ufs.length === 1) return placeName(ufs[0] as UfCode);
  if (ufs.length <= 3) return joinList(ufs);
  return t('stillCounting.nStates', { N: ufs.length });
}

/**
 * "Ainda em apuração" (§7.9): depois da definição presidencial, a linha "Governadores" leva à aba Governadores,
 * mantendo o lugar em foco (`setOffice`, §5.4.2). Não renderiza sem definição, sem UF apurando, com
 * `features.governors = false`, no `land` nem na TV; nunca é podada por altura no desktop (§4.5.3).
 */
export function StillCountingList({ decided }: StillCountingListProps) {
  const view = useView();
  const governorsOn = useConfig().features.governors;
  const mode = useLayout().mode;
  if (!decided || view === null || !governorsOn || mode === 'land' || mode === 'tv') return null;
  const ufs = governorsCounting(view.governor);
  if (ufs.length === 0) return null;
  const detail = stillCountingDetail(ufs);
  return (
    <nav class="still-counting-list" data-testid="still-counting-list" aria-labelledby="still-counting-title">
      <p id="still-counting-title" class="still-counting-list__title">
        {t('stillCounting.title')}
      </p>
      <button
        type="button"
        class="still-counting-list__row"
        aria-label={t('stillCounting.aria', { LISTA: detail })}
        onClick={() => setOffice('governor')}
      >
        <LiveDot variant="blink" />
        <b class="still-counting-list__name">{t('stillCounting.governors')}</b>
        <span class="still-counting-list__detail">{t('stillCounting.detail', { LISTA: detail })}</span>
        <span class="still-counting-list__arrow">
          <Icon name="chevron-right" size={14} />
        </span>
      </button>
    </nav>
  );
}
